/*
 * Why an SSE stream was refused. EventSource hides the status of a failed
 * connection: an answer other than 200 just closes it (readyState CLOSED). The
 * center limits the streams (decision 51: 12 per user, 256 in all on each of
 * /api/events, /api/admin/relay/events, /api/admin/hosts/events, /api/chat/events)
 * and refuses one more with 429 and Retry-After. After a refusal the client asks
 * once with fetch: 429 → wait Retry-After before the next try instead of
 * hammering the limit, and read limit / open from the body for the "Live data
 * paused" text; a 200 is closed at once (it was only a question).
 */

export interface SseRefusal {
  /** the status of the probe; 0 = no answer (network) */
  status: number
  /** 429: wait this long before the next try (Retry-After, clamped 1 s – 10 min); else undefined */
  retryAfterMs?: number
  /** 429 of a center that says (SSE-TABS): its per-user limit and how many streams the user has open */
  limit?: number
  open?: number
}

/** Retry-After without a value: half a minute. */
export const DEFAULT_RETRY_AFTER_MS = 30_000

/** Retry-After in seconds or as an HTTP date → ms from now. */
export function retryAfterMs(h: string | null, now = Date.now()): number {
  if (!h) return DEFAULT_RETRY_AFTER_MS
  const s = Number(h)
  const ms = Number.isFinite(s) ? s * 1000 : Date.parse(h) - now
  if (!Number.isFinite(ms)) return DEFAULT_RETRY_AFTER_MS
  return Math.min(600_000, Math.max(1000, ms))
}

/** Ask the stream's URL once to learn why it was refused. */
export async function probeSse(path: string): Promise<SseRefusal> {
  const ac = new AbortController()
  try {
    const r = await fetch(path, { credentials: 'same-origin', headers: { Accept: 'text/event-stream' }, signal: ac.signal, cache: 'no-store' })
    const out: SseRefusal = { status: r.status }
    if (r.status === 429) {
      out.retryAfterMs = retryAfterMs(r.headers.get('Retry-After'))
      try {
        const b = (await r.json()) as { limit?: unknown; open?: unknown }
        if (typeof b.limit === 'number') out.limit = b.limit
        if (typeof b.open === 'number') out.open = b.open
      } catch {
        // an older center: no counts in the body
      }
    }
    return out
  } catch {
    return { status: 0 }
  } finally {
    // a 200 is an open stream: close it at once, it was only a question
    ac.abort()
  }
}
